import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useDrag from "../../../hooks/useDrag";
import AppBar from "./AppBar";
import EpmsImage from "../../../../Common/EpmsImage";
import Button from "@material-ui/core/Button";
import {makeStyles} from "@material-ui/core/styles";
import ActionsContainer from "./ActionsContainer";
import ToolbarContainer from "./ToolbarContainer";
import {usePriceList} from "../../../../Providers/PriceListProvider";
import useResizeObserver from "../../../hooks/useResizeObserver";
import useImagePreview from "../../../hooks/useImagePreview";
import SubHeader from "../../../Components/SubHeader";
import ToolbarContent from "./ToolbarContent";
import Box from "@material-ui/core/Box";
import {calculateCropRect, getCropRectFillInByProduct} from "../../../../../utils";
import FormControlLabel from "@material-ui/core/FormControlLabel/FormControlLabel";
import Switch from "@material-ui/core/Switch/Switch";
import {Trans, useTranslation} from "react-i18next";
import ToolbarToast from "./ToolbarToast";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import {getProductOrientationByCropRect, productCropRectWillHaveWhiteBorders} from "../../../../../lib/imageutils";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: 'white',
    height: '148px',
    zIndex: '2'
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'space-around'
  },
  orderLineImage: {
    flexGrow: 1,
    flexBasis: 0,
    position: 'relative',
  },
  cropContainer: {
    overflow: 'hidden'
  },
  qualityWarning: {
    position: 'absolute',
    color: 'yellow',
    right: 0,
    bottom: 0,
  }
}));


export default ({orderLine, onCancel, setCropRect}) => {
  const {getProductByUuid, isWhiteBorderProduct} = usePriceList();
  const classes = useStyles();
  const [t] = useTranslation();
  const [showFitInWarning, setShowFitInWarning] = useState(false);

  const product = getProductByUuid(orderLine.product.uuid)

  const [localCropRect, setLocalCropRect] = useState(() => {
      if (orderLine.image.cropRect) {
        return orderLine.image.cropRect;
      }
      // Get crop rect for fill in:
      return getCropRectFillInByProduct(orderLine.files[0].size, product, isWhiteBorderProduct(product));
  });

  const handleRotatePaper = useCallback( () => {
    const newOrientation = getProductOrientationByCropRect(product, localCropRect) == 'landscape' ? 'portrait' : 'landscape'
    setLocalCropRect(getCropRectFillInByProduct(orderLine.files[0].size, product, isWhiteBorderProduct(product), newOrientation))
  }, [product, localCropRect]);

  const handleCloseFitInWarning = useCallback( () => {
    setShowFitInWarning(false);
  }, [])

  useEffect( () => {
    if (!localCropRect) {
      return;
    }

    // Check if we should show a white pixel warning:
    if (productCropRectWillHaveWhiteBorders(product, localCropRect, orderLine.files[0].size)) {
      setShowFitInWarning(true)
    }
    else {
      setShowFitInWarning(false)
    }

  }, [localCropRect])


  return <div className={classes.mainContainer}>
    <AppBar onCancel={onCancel} />

    <Box flex={1} p={2} display={'flex'} className={classes.cropContainer}>
      <ImageCrop orderLine={orderLine} onSetCropRect={setLocalCropRect} localCropRect={localCropRect}
               onFlipFormat={() => setFlippedFormat((flippedFormat) => !flippedFormat)}
    />
    </Box>
    {/*<ImagePreview orderLine={orderLine}/>*/}

    <ToolbarContainer>
      {showFitInWarning && <ToolbarToast onClick={handleCloseFitInWarning}>
        <Trans>Your picture does not fit the print format perfectly, and may get white borders. Resize the picture to avoid this.</Trans>
      </ToolbarToast>}
      <ToolbarContent>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
          <FormControlLabel
            control={<Switch checked={getProductOrientationByCropRect(product, localCropRect) == 'landscape'}
                             onChange={handleRotatePaper} color="primary" />}
            label={t('Landscape orientation')}
          />
        </Box>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={onCancel}><Trans>Cancel</Trans></Button>
        <Button color="primary" onClick={() => setCropRect(localCropRect)}><Trans>Done</Trans></Button>
      </ActionsContainer>
    </ToolbarContainer>
  </div>
}

const ImageCrop = ({orderLine, onSetCropRect, localCropRect}) => {
  const { getProductByUuid, isWhiteBorderProduct } = usePriceList();
  const ref = useRef(null);
  const containerSize = useResizeObserver(ref);
  const product = getProductByUuid(orderLine.product.uuid)
  const whiteBorder = useMemo(() => {
    return isWhiteBorderProduct(product);
  }, [product])

  console.log('Container size', containerSize, localCropRect);
  const {paper, imageContainer, image, qualityWarning} = useImagePreview(orderLine.files[0].size, containerSize, product, localCropRect, whiteBorder, 'fit-in')
  const imageContainerRef = useRef();
  imageContainerRef.current = imageContainer;
  const imageRef = useRef();
  const classes = useStyles();


  const handleChange = useCallback((position) => {
    console.log('Handle change', position);
    const cropRect = calculateCropRect(position, file.size, imageContainerRef.current);
    console.log('handleChange - Position', position, cropRect);
    onSetCropRect(cropRect);

  }, [imageContainerRef]);


  const options = useMemo(() => {
    return {
      containerSize: imageContainer,
      onChange: handleChange,
      adaption: 'fit-in'
    }
  }, [imageContainer, image])

  const startingPosition = useMemo(() => {
    console.log('Image size is', image, localCropRect);
    return {...image}
  }, [image, localCropRect])

  const {
    isDragging,
    picturePosition,
    eventHandlers,
  } = useDrag(imageRef, startingPosition, options)

  const file = orderLine.files[0];
  const filter = orderLine.image.filter

  const style = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: containerSize.width + 'px',
    height: containerSize.height + 'px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const paperStyle = {
    width: paper.width,
    height: paper.height,
    boxShadow: '0px 0px 1px rgba(0,0,0,.3), 0px 0px 10px rgba(0,0,0,.3)',
    backgroundColor: 'white',
    position: 'relative'
  };

  const imageContainerStyle = {
    width: imageContainer.width + 'px',
    height: imageContainer.height + 'px',
    top: imageContainer.y + 'px',
    left: imageContainer.x + 'px',
    overflow: 'hidden',
    position: 'absolute',
    // For some reason, this fixes overflow hidden issue in Safari
    zIndex: 0
  }

  const imageStyle = {
    position: 'absolute',
    width: picturePosition.width + 'px',
    height: picturePosition.height + 'px',
    top: picturePosition.y + 'px',
    left: picturePosition.x + 'px',
    transform: 'translateZ(0)'
  };

  const imageDraggingContainerStyle = {
    ...imageContainerStyle,
    overflow: 'visible',
  }

  const imageDraggingStyle = {
    ...imageStyle,
    opacity: isDragging ? 0.5 : 0
  };


  console.log('Image crop position', picturePosition, imageDraggingContainerStyle, imageContainerStyle);

  return <div className={classes.orderLineImage} ref={ref}>
    <div style={style}>
      <div style={paperStyle}>
        <div style={imageContainerStyle}>
          <div style={imageStyle}>
            <EpmsImage  style={{width: '100%', height: '100%'}} uuid={file.uuid} effect={filter} size={640} />
          </div>
          {qualityWarning && !isDragging && <IconButton className={classes.qualityWarning}><Icon>warning</Icon></IconButton>}
        </div>
        <div style={imageDraggingContainerStyle}>
          <div ref={imageRef} style={imageDraggingStyle} {...eventHandlers}>
            <EpmsImage  style={{width: '100%', height: '100%'}} uuid={file.uuid} effect={filter} size={640} />
          </div>
        </div>
      </div>
    </div>
  </div>
}